import React, { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PurchaseMissingItem, PurchaseV2Item } from 'apis/merchant-gateway/merchant-analytics/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useLocaleUtils } from 'common/hooks';
import { localize } from 'common/utils';

import {
  ItemCount,
  ItemInfo,
  ItemInfoRow,
  ItemName,
  ItemOption,
  ItemOptionCount,
  ItemOptionPrice,
  ItemOptionValue,
  ItemOptions,
  ItemPrice,
  ItemRoot,
  MultiplicationSign,
  OriginalPurchaseWeight,
  PurchaseWeight,
  StyledArrowDownAndRight,
  SubstitutionItem,
  SubstitutionItemName,
} from './PurchaseBreakdown.styled';

const PurchaseBreakdownItem = ({
  item,
  currency,
  substitution,
}: {
  item: PurchaseV2Item;
  currency: Currency;
  substitution?: PurchaseMissingItem;
}) => {
  const { locale } = useIntl();
  const { formatWeightQuantity } = useLocaleUtils();

  const isWeightChange = substitution?.itemId === item.itemId && !!item.weightedItem && !!substitution.weightedItem;

  return (
    <ItemRoot>
      <ItemInfo>
        {item.weightedItem ? (
          <>
            <ItemInfoRow>
              <PurchaseWeight>
                {formatWeightQuantity(item.weightedItem.weightInGrams, {
                  unitDisplay: 'narrow',
                })}{' '}
                <MultiplicationSign />
              </PurchaseWeight>
              <ItemName>
                {localize(item.product.names, locale)}
                {!!item.weightedItem.requestedCount && (
                  <>
                    {' '}
                    (
                    <FormattedMessage
                      id="purchases.display-unit-pc"
                      values={{
                        unit: item.weightedItem.requestedCount,
                      }}
                    />
                    )
                  </>
                )}
              </ItemName>
              <ItemPrice>
                <MonetaryAmount amount={item.pricing.beforeDiscountPrice?.gross ?? 0} currency={currency} />
              </ItemPrice>
            </ItemInfoRow>

            {isWeightChange && (
              <ItemInfoRow>
                <OriginalPurchaseWeight>
                  {!!substitution.weightedItem?.weightInGrams &&
                    formatWeightQuantity(substitution.weightedItem.weightInGrams, {
                      unitDisplay: 'narrow',
                    })}
                  {!!substitution.weightedItem?.requestedCount && (
                    <>
                      {' '}
                      (
                      <FormattedMessage
                        id="purchases.display-unit-pc"
                        values={{
                          unit: substitution.weightedItem.requestedCount,
                        }}
                      />
                      )
                    </>
                  )}
                </OriginalPurchaseWeight>
              </ItemInfoRow>
            )}
          </>
        ) : (
          <ItemInfoRow>
            <ItemCount>
              {item.count} <MultiplicationSign />
            </ItemCount>
            <ItemName>{localize(item.product.names, locale)}</ItemName>
            <ItemPrice>
              <MonetaryAmount amount={item.pricing.beforeDiscountPrice?.gross ?? 0} currency={currency} />
            </ItemPrice>
          </ItemInfoRow>
        )}
      </ItemInfo>

      {substitution && !isWeightChange && (
        <ItemInfoRow>
          <SubstitutionItem>
            <StyledArrowDownAndRight /> <FormattedMessage id="purchases.substitution-for" />:{' '}
            <SubstitutionItemName>{localize(substitution.product.names, locale)}</SubstitutionItemName>
          </SubstitutionItem>
        </ItemInfoRow>
      )}

      <ItemOptions>
        {item.options.map(({ optionId, values }) =>
          values.map(value => (
            <Fragment key={`${optionId}-${value.valueId}`}>
              {value.pricing?.optionValuesOnlyPrices.some(valuePrice => !!valuePrice.price.gross) ? (
                value.pricing?.optionValuesOnlyPrices.map((valuePrice, index) => (
                  <ItemOption key={`${optionId}-${value.valueId}-${index}`}>
                    <ItemOptionCount>
                      {valuePrice.count} <MultiplicationSign />
                    </ItemOptionCount>
                    <ItemOptionValue>{localize(value.product.names, locale)}</ItemOptionValue>
                    <ItemOptionPrice>
                      (<MonetaryAmount amount={valuePrice.price.gross} currency={currency} />)
                    </ItemOptionPrice>
                  </ItemOption>
                ))
              ) : (
                <ItemOption>
                  <ItemOptionCount>
                    {value.count} <MultiplicationSign />
                  </ItemOptionCount>
                  <ItemOptionValue>{localize(value.product.names, locale)}</ItemOptionValue>
                </ItemOption>
              )}
              {!!value.pricing?.deposit.gross && (
                <ItemOption>
                  <ItemOptionCount>
                    {value.count} <MultiplicationSign />
                  </ItemOptionCount>
                  <ItemOptionValue>
                    <FormattedMessage id="purchases.deposit" />
                  </ItemOptionValue>
                  <ItemOptionPrice>
                    (<MonetaryAmount amount={value.pricing.deposit.gross} currency={currency} />)
                  </ItemOptionPrice>
                </ItemOption>
              )}
            </Fragment>
          )),
        )}
        {!!item.pricing.itemDeposit?.gross && (
          <ItemOption>
            <ItemOptionCount>
              {item.count} <MultiplicationSign />
            </ItemOptionCount>
            <ItemOptionValue>
              <FormattedMessage id="purchases.deposit" />
            </ItemOptionValue>
            <ItemOptionPrice>
              (<MonetaryAmount amount={item.pricing.itemDeposit.gross} currency={currency} />)
            </ItemOptionPrice>
          </ItemOption>
        )}
      </ItemOptions>
    </ItemRoot>
  );
};

export default PurchaseBreakdownItem;
